import React from "react";

interface Feature {
  title: string;
  description: string;
  icon: string;
  color: string;
}

interface SlideContent {
  title?: string;
  subtitle?: string;
  features?: string | Feature[];
  bgColor?: string;
  onEdit?: (content: SlideContent) => void;
}

const defaultFeatures: Feature[] = [
  {
    title: "",
    description: "",
    icon: "✨",
    color: "#22c55e",
  },
  {
    title: "",
    description: "",
    icon: "🎯",
    color: "#16a34a",
  },
  {
    title: "",
    description: "",
    icon: "⚡",
    color: "#15803d",
  },
];

const EditableDiv: React.FC<{
  text: string;
  onEdit: (value: string) => void;
  className?: string;
}> = ({ text, onEdit, className = "" }) => {
  return (
    <div
      contentEditable
      suppressContentEditableWarning
      onBlur={(e) => onEdit(e.currentTarget.textContent || "")}
      className={`outline-none ${className}`}
    >
      {text}
    </div>
  );
};

const FeatureSlide1 = {
  id: "feature-slide1",
  name: "Feature Highlights Style 1",
  fields: [
    {
      id: "title",
      label: "Title",
      type: "text",
      defaultValue: "",
    },
    {
      id: "subtitle",
      label: "Subtitle",
      type: "text",
      defaultValue: "",
    },
    {
      id: "features",
      label: "Features",
      type: "text",
      defaultValue: JSON.stringify(defaultFeatures),
    },
    {
      id: "bgColor",
      label: "Background Color",
      type: "text",
      defaultValue:
        "linear-gradient(135deg, #166534 0%, #15803d 50%, #16a34a 100%)",
    },
  ],
  render: (content: SlideContent = {}) => {
    const {
      title = "",
      subtitle = "",
      features = JSON.stringify(defaultFeatures),
      bgColor = "linear-gradient(135deg, #166534 0%, #15803d 50%, #16a34a 100%)",
      onEdit = () => {},
    } = content;

    let parsedFeatures = defaultFeatures;
    try {
      if (typeof features === "string") {
        parsedFeatures = JSON.parse(features);
      } else if (Array.isArray(features)) {
        parsedFeatures = features;
      }
    } catch (error) {
      console.warn("Error parsing features:", error);
    }

    const handleFeatureEdit = (index: number, field: string, value: string) => {
      const updatedFeatures = [...parsedFeatures];
      updatedFeatures[index] = {
        ...updatedFeatures[index],
        [field]: value,
      };
      onEdit({ ...content, features: JSON.stringify(updatedFeatures) });
    };

    // Function to calculate text size based on content length
    const getTextSize = (text: string, type: "title" | "description") => {
      if (type === "title") {
        if (text.length <= 15) return "text-sm";
        if (text.length <= 25) return "text-xs";
        return "text-xs scale-90";
      }
      if (text.length <= 30) return "text-xs";
      if (text.length <= 50) return "text-xs scale-95";
      return "text-xs scale-90";
    };

    return (
      <div className="w-full h-full relative overflow-hidden text-white">
        {/* Dynamic Background */}
        <div
          className="absolute inset-0 transition-all duration-1000"
          style={{ background: bgColor }}
        >
          {/* Animated Patterns */}
          <div className="absolute inset-0 opacity-10">
            <div
              className="absolute w-full h-full"
              style={{
                backgroundImage: `
                  radial-gradient(circle at 20% 20%, ${parsedFeatures[0].color}80 0%, transparent 25%),
                  radial-gradient(circle at 80% 80%, ${parsedFeatures[1].color}80 0%, transparent 25%),
                  radial-gradient(circle at 50% 50%, ${parsedFeatures[2].color}80 0%, transparent 35%)
                `,
              }}
            />
          </div>

          {/* Animated Lines */}
          <div className="absolute inset-0 opacity-20">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="absolute w-full h-1 bg-white/20 animate-pulse"
                style={{
                  top: `${30 + i * 30}%`,
                  transform: `rotate(${i * 60}deg)`,
                  animationDelay: `${i * 0.2}s`,
                }}
              />
            ))}
          </div>
        </div>

        {/* Content Container */}
        <div className="relative z-10 flex flex-col h-full p-12">
          {/* Header Section */}
          <div className="text-center mb-12 space-y-4">
            <EditableDiv
              text={title}
              onEdit={(value) => onEdit({ ...content, title: value })}
              className="text-5xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r 
                         from-white via-green-100 to-white animate-text-shine"
            />
            <EditableDiv
              text={subtitle}
              onEdit={(value) => onEdit({ ...content, subtitle: value })}
              className="text-2xl text-green-100/90 font-light"
            />
          </div>

          {/* Features Container */}
          <div className="flex-1 flex justify-center items-center gap-8">
            {parsedFeatures.slice(0, 3).map((feature, idx) => (
              <div
                key={idx}
                className="group relative w-1/3 min-h-[300px] bg-white/5 backdrop-blur-md rounded-3xl p-8
                          transform transition-all duration-500 hover:scale-105 hover:-translate-y-2
                          border border-white/10 hover:border-white/30
                          hover:shadow-[0_0_30px_rgba(255,255,255,0.1)]"
                style={{
                  background: `linear-gradient(165deg, 
                    ${feature.color}20 0%, 
                    ${feature.color}10 40%, 
                    transparent 100%
                  )`,
                }}
              >
                {/* Icon Container */}
                <div className="relative mb-6">
                  <div
                    className="absolute -inset-1 bg-white/20 rounded-full blur-md opacity-0 
                                group-hover:opacity-100 transition-opacity duration-500"
                  />
                  <EditableDiv
                    text={feature.icon}
                    onEdit={(value) => handleFeatureEdit(idx, "icon", value)}
                    className="text-5xl transform transition-all duration-500 
                              group-hover:scale-110 group-hover:rotate-12"
                  />
                </div>

                {/* Title */}
                <EditableDiv
                  text={feature.title}
                  onEdit={(value) => handleFeatureEdit(idx, "title", value)}
                  className="text-2xl font-bold text-white mb-4 leading-tight
                            bg-gradient-to-r from-white to-green-100 bg-clip-text text-transparent"
                />

                {/* Description */}
                <EditableDiv
                  text={feature.description}
                  onEdit={(value) =>
                    handleFeatureEdit(idx, "description", value)
                  }
                  className="text-lg text-white/80 leading-relaxed
                            transform transition-all duration-300 group-hover:text-white"
                />

                {/* Decorative Elements */}
                <div
                  className="absolute top-0 right-0 w-32 h-32 opacity-20 rounded-full blur-3xl 
                              transition-all duration-500 group-hover:opacity-30"
                  style={{ background: feature.color }}
                />

                {/* Bottom Highlight */}
                <div className="absolute bottom-0 left-0 w-full h-1 overflow-hidden">
                  <div
                    className="w-full h-full bg-gradient-to-r from-transparent via-white/40 to-transparent
                                transform -translate-x-full group-hover:translate-x-full transition-transform 
                                duration-1000"
                  />
                </div>

                {/* Corner Accent */}
                <div className="absolute top-0 right-0 w-16 h-16">
                  <div
                    className="absolute inset-0 transform rotate-45 translate-x-1/2 -translate-y-1/2
                                bg-gradient-to-br from-white/5 to-transparent"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  },
};

export default FeatureSlide1;
